import React from 'react';
import { Box, Flex } from 'rebass/styled-components';
import { Redirect, Switch, Route, useRouteMatch, useHistory } from 'react-router-dom';

import LoadingFullPage from 'components/molecules/LoadingFullPage';
import { ContactsLinkedInSync } from 'pages/contactsLinkedInSync';
import { OnboardingMfa } from 'pages/Onboarding/steps/mfa';
import { AllyCompletionMessage } from './components/AllyCompletionMessage';
import AllySignupLayout from './components/AllySignupLayout';
import { SignUp } from './components/signUp';
import { Profile } from './components/profile';
import { AllyIntegrations } from './components/integrations';
import { LoggedInAlly } from './components/loggedInAlly';
import { useAllySignup } from './hooks/useAllySignup';

export const AllySignup = () => {
  const { path } = useRouteMatch();
  const history = useHistory();
  const {
    user,
    network,
    url,
    onboardingSteps,
    currentStepIndex,
    steps,
    isShareContactsRoute,
    isMfaRoute,
    isShareContactsLoading,
    handleShareContacts,
    shouldRedirectToStayTuned,
    shouldRedirectToHome,
    shouldRedirectToShareContacts,
    shareContactsRedirectUrl,
    shouldRedirectToMfa,
    mfaRedirectUrl,
    shouldBlockForMfaStatus,
  } = useAllySignup();

  if (shouldRedirectToStayTuned) {
    return <Redirect to="/stay-tuned" />;
  }

  if (shouldRedirectToHome) {
    return <Redirect to="/" />;
  }

  if (shouldRedirectToShareContacts) {
    return <Redirect to={shareContactsRedirectUrl} />;
  }

  if (shouldRedirectToMfa) {
    return <Redirect to={mfaRedirectUrl} />;
  }

  // Fail closed: hold past-account steps while MFA status can't be confirmed (loading/error),
  // so a failed status fetch can't let an ally reach integrations without enrolling.
  if (shouldBlockForMfaStatus) {
    return <LoadingFullPage />;
  }

  return (
    <AllySignupLayout
      network={network}
      onboardingSteps={isShareContactsRoute || isMfaRoute ? null : onboardingSteps}
      currentStepIndex={currentStepIndex}
      steps={steps}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '160px',
          flex: 1,
          height: '100%',
        }}
      >
        <Switch>
          <Route exact path={`${path}`}>
            <Box maxWidth="400px" m="0px auto" pb="40px">
              <SignUp routePath={url} network={network} />
            </Box>
          </Route>
          <Route exact path={`${path}/mfa`}>
            {/* Full-screen MFA enrollment for the invite funnel (this flow isn't modal-based,
                unlike the shared-company-list funnel). Mirrors the network-admin step. */}
            <Box maxWidth="432px" m="0px auto" pb="40px">
              <OnboardingMfa network={network} onComplete={() => history.push(`${url}/integrations`)} topGap="0px" />
            </Box>
          </Route>
          <Route exact path={`${path}/profile/:mode`}>
            <Box maxWidth="400px" m="0px auto" pb="40px">
              <Profile user={user} network={network} />
            </Box>
          </Route>
          <Route exact path={`${path}/integrations`}>
            <Box minWidth="600px" maxWidth="600px" m="0px auto" pb="40px">
              <AllyIntegrations network={network} />
            </Box>
          </Route>
          <Route exact path={`${path}/complete`}>
            <Box maxWidth="400px" m="0px auto" pb="40px">
              <AllyCompletionMessage network={network} />
            </Box>
          </Route>
          <Route exact path={`${path}/linkedin/:action`}>
            <ContactsLinkedInSync isAllyOnboarding />
          </Route>
          <Route exact path={`${path}/share-contacts`}>
            <Box maxWidth="400px" m="0px auto" pb="40px">
              <LoggedInAlly
                network={network}
                user={user}
                onShareContacts={handleShareContacts}
                isShareContactsLoading={isShareContactsLoading}
              />
            </Box>
          </Route>
        </Switch>
      </Flex>
    </AllySignupLayout>
  );
};

export default AllySignup;
